<i18n src="@/locales/models/estimated_harvest.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/producer/projections/estimated_harvest/show_harvest_process.yml"
  lang="yaml"
></i18n>
<template>
  <div class="mt-6">
    <h1 class="page-title">
      <i class="fa-solid fa-apple-crate"></i><span class="space-icon"></span
      >{{ $t("labels.estimated_harvests_title") }}
    </h1>
    <div class="flex flex-row justify-end">
      <new-button
        @click.prevent="goToNewEstimatedHarvest()"
        :label="$t('labels.new_estimated_harvest_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <ag-grid-vue
        style="width: 100%; height: 600px"
        class="ag-theme-alpine w-full h-full"
        @grid-ready="onGridReady"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="estimated_harvests_modified"
      ></ag-grid-vue>
    </div>
  </div>
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import ManageRequester from "@/views/producer/requesters/projections/estimated_harvest/manage_estimated_harvest.js";
import ShowProcessTabsMixin from "@/components/views/producer/projections/estimated_harvest/show_process/show_process_tabs_mixin.js";

export default {
  name: "ShowHarvestProcessEstimatedHarvests",
  components: { AgGridVue },
  mixins: [ShowProcessTabsMixin],
  data() {
    return {
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t("model.estimated_harvest.week"),
          field: "week",
          filter: "agNumberColumnFilter",
          width: 50
        },
        {
          headerName: this.$t("model.estimated_harvest.crop_field"),
          field: "crop_field.name"
        },
        ...this.displayCropFieldSector(),
        {
          headerName: this.$t("model.estimated_harvest.num_packages"),
          field: "num_packages",
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return `${this.numberWithPrecision(params.value)} ${
              params.data.harvest_process_crop_package.name
            }s`;
          }
        },
        ...this.harvest_process_crop_size_groups.map(
          harvest_process_crop_size_group => {
            return {
              headerName: harvest_process_crop_size_group.name + " (%)",
              field: `${harvest_process_crop_size_group.id}`,
              filter: "agNumberColumnFilter"
            };
          }
        ),
        {
          headerName: "",
          filter: false,
          width: 300,
          cellRendererSelector: params => {
            const destroy = this.destroy;
            const edit = this.goToEditEstimatedHarvest;
            const ActionButtons = {
              template: `<div>
                <edit-button @click.prevent="this.goToEditEstimatedHarvest()"
                  label="${this.$t("labels.edit_button")}"
                />
                <span class="space-actions"></span>
                <destroy-button
                  v-if="${!params.data.proc_button_destroy}"
                  :disabled_button="false"
                  @destroyEvent="this.destroyEstimatedHarvest"
                  label="${this.$t("labels.destroy_button_label")}"
                  title="${this.$t("labels.destroy_button_title")}"
                  message="${this.$t("labels.destroy_button_message")}"
                  label_confirm="${this.$t(
                    "labels.destroy_button_label_confirm"
                  )}"
                />
                <destroy-button-processing
                  v-else
                  :label="$t('labels.destroy_button_label')"
                />
              </div>`,
              methods: {
                goToEditEstimatedHarvest: function () {
                  edit(params.data);
                },
                destroyEstimatedHarvest: function () {
                  destroy(params.data);
                }
              }
            };
            return { component: ActionButtons };
          }
        }
      ]
    };
  },
  props: {
    estimated_harvests: {
      type: Array,
      required: true
    },
    harvest_process_crop_size_groups: {
      type: Array,
      required: true
    }
  },
  computed: {
    estimated_harvests_modified: function () {
      return this.estimated_harvests.map(estimated_harvest => {
        let estimated_harvest_groups = {};
        this.harvest_process_crop_size_groups.forEach(
          harvest_process_crop_size_group => {
            estimated_harvest_groups[harvest_process_crop_size_group.id] =
              estimated_harvest.estimated_harvest_groups.find(
                ehg =>
                  ehg.harvest_process_crop_size_group_id ==
                  harvest_process_crop_size_group.id
              )?.percent || "";
          }
        );
        return {
          ...estimated_harvest,
          ...estimated_harvest_groups
        };
      });
    }
  },
  methods: {
    goToNewEstimatedHarvest: function () {
      this.$router.push({
        name: this.routeName("new_estimated_harvest"),
        params: {
          producer_id: this.producer_id,
          harvest_process_id: this.harvest_process_id
        }
      });
    },
    goToEditEstimatedHarvest: function (estimated_harvest) {
      this.$router.push({
        name: this.routeName("edit_estimated_harvest"),
        params: {
          producer_id: this.producer_id,
          harvest_process_id: this.harvest_process_id,
          estimated_harvest_id: estimated_harvest.id
        }
      });
    },
    displayCropFieldSector: function () {
      let crop_field_sectors = this.estimated_harvests
        .map(estimated_harvest => estimated_harvest.crop_field_sector?.name)
        .filter((v, i, a) => v !== undefined && v !== null && a.indexOf(v) === i)

      if (crop_field_sectors.length > 1) {
        return [
          {
            headerName: this.$t("model.estimated_harvest.crop_field_sector"),
            field: "crop_field_sector.name"
          }
        ];
      } else return [];
    },
    destroy: async function (estimated_harvest) {
      estimated_harvest.proc_button_destroy = true;
      try {
        let response = await ManageRequester.destroy(
          this.producer_id,
          estimated_harvest.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.$parent.$parent.estimated_harvests.splice(
          this.$parent.$parent.estimated_harvests.indexOf(estimated_harvest),
          1
        );
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      estimated_harvest.proc_button_destroy = false;
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 150
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  }
};
</script>
